import { Button, Input, Modal, Spin, Table, Tabs, Tag, message } from "antd";
import { useEffect, useState } from "react";
import { FaSearch, FaTrash } from "react-icons/fa";
import { apiHandle } from "../../config/apiHandle";

const { TabPane } = Tabs;
const { confirm } = Modal;

const FormSubmissions = () => {
  const [activeTab, setActiveTab] = useState("newsletter");
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [forms, setForms] = useState([]);

  const fetchForms = async (type) => {
    try {
      setLoading(true);
      const response = await apiHandle.get(`admin/get-forms?type=${type}`);
      setForms(response.data.data.forms);
    } catch (error) {
      console.error("Error fetching forms:", error);
      message.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchForms(activeTab);
  }, [activeTab]);

  // Handle delete form submission
  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await apiHandle.delete(`admin/delete-form/${id}`);
      message.success("Form submission deleted successfully");
      // Refresh form list after deletion
      fetchForms(activeTab);
    } catch (error) {
      console.error("Error deleting form:", error);
      message.error("Failed to delete submission");
    } finally {
      setDeletingId(null);
    }
  };

  const getColumns = () => {
    const actionColumn = {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          type="danger"
          icon={<FaTrash />}
          onClick={() => handleDelete(record._id)}
          loading={deletingId === record._id}
          size="small"
        >
          Delete
        </Button>
      ),
    };

    const basicColumns = [
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
        sorter: (a, b) => a.email.localeCompare(b.email),
      },
      {
        title: "Submitted At",
        dataIndex: "createdAt",
        key: "createdAt",
        render: (date) => new Date(date).toLocaleString(),
        sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      },
    ];

    if (activeTab === "contact") {
      return [
        ...basicColumns,
        {
          title: "Name",
          key: "name",
          render: (record) => `${record.first_name} ${record.last_name}`,
          sorter: (a, b) =>
            `${a.first_name} ${a.last_name}`.localeCompare(
              `${b.first_name} ${b.last_name}`
            ),
        },
        {
          title: "Phone",
          dataIndex: "phone",
          key: "phone",
        },
        {
          title: "Subject",
          dataIndex: "subject",
          key: "subject",
        },
        {
          title: "Message",
          dataIndex: "message",
          key: "message",
          ellipsis: true,
        },
        actionColumn,
      ];
    }

    return [...basicColumns, actionColumn];
  };

  const filteredData = forms.filter((form) =>
    Object.values(form).some((value) =>
      String(value).toLowerCase().includes(searchText.toLowerCase())
    )
  );

  return (
    <div className="container mx-auto p-6 bg-white rounded-lg shadow">
      <h1 className="text-3xl font-bold text-center mb-4">
        Form Submissions Management
      </h1>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        tabBarExtraContent={
          <Input
            placeholder="Search submissions..."
            prefix={<FaSearch className="text-gray-400" />}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
          />
        }
      >
        <TabPane
          tab={
            <span>
              <Tag color="blue">Newsletter</Tag>
              Newsletter Subscriptions
            </span>
          }
          key="newsletter"
        >
          <Table
            columns={getColumns()}
            dataSource={filteredData}
            loading={loading}
            rowKey="_id"
            pagination={{ pageSize: 10 }}
            scroll={{ x: true }}
          />
        </TabPane>

        <TabPane
          tab={
            <span>
              <Tag color="green">Contact</Tag>
              Contact Forms
            </span>
          }
          key="contact"
        >
          <Table
            columns={getColumns()}
            dataSource={filteredData}
            loading={loading}
            rowKey="_id"
            pagination={{ pageSize: 10 }}
            scroll={{ x: true }}
          />
        </TabPane>
      </Tabs>

      {loading && (
        <div className="text-center mt-4">
          <Spin size="large" />
          <p className="mt-2 text-gray-600">Loading submissions...</p>
        </div>
      )}
    </div>
  );
};

export default FormSubmissions;
